// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFaction } from "@/contexts/FactionContext";
import "./NavBar.css";

const API = process.env.REACT_APP_BACKEND_URL;

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/games", label: "Games" },
  { to: "/two-player", label: "Two Player" },
  { to: "/four-player", label: "Four Player" },
  { to: "/puzzles", label: "Puzzles" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/settings", label: "Settings" },
  { to: "/profile", label: "Profile" },
  { to: "/store", label: "Store" },
  { to: "/cards", label: "Cards" },
  { to: "/about", label: "About" },
];

export default function NavBar() {
  const { user, loading, login, logout, authEnabled } = useAuth();
  // Pledge state drives the header's color reactivity. Pre-pledge:
  // buttons blend into the parchment header (low contrast, subtle).
  // Post-pledge: buttons pick up the current faction's canonical
  // colors via the [data-faction="…"] variables already defined in
  // App.css (mirror of factionThemes.js) — no new hexes are invented
  // here; NavBar.css just switches which existing var it consumes.
  const { hasPledged } = useFaction();
  // Gold/gems pill in .nav-right — mirrors the fetch pattern used by
  // Profile.jsx (which reads from /api/store/inventory). Only fires
  // when a user is logged in; polls quietly so a purchase or reward
  // credit propagates to the header within ~30 s without a page
  // reload. Silent on any failure — the header is a decorative
  // surface, not critical UX.
  const [currency, setCurrency] = useState(null);
  useEffect(() => {
    if (!user) { setCurrency(null); return; }
    let cancelled = false;
    const fetchInv = async () => {
      try {
        const { data } = await axios.get(`${API}/api/store/inventory`, {
          withCredentials: true, timeout: 8000,
        });
        if (!cancelled) setCurrency(data?.currency || { gold: 0, gems: 0 });
      } catch { /* silent */ }
    };
    fetchInv();
    const iv = setInterval(fetchInv, 30_000);
    // Immediate refetch when a game ends (dispatched by Game.jsx).  This
    // makes the currency pill catch up within a network round-trip
    // instead of waiting up to 30 s for the next poll tick.
    const onGameEnded = () => { fetchInv(); };
    window.addEventListener("emergent:game-ended", onGameEnded);
    // Store purchases and Treasure Wheel spins announce a balance change.
    window.addEventListener("4xlc:currency-changed", onGameEnded);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("emergent:game-ended", onGameEnded);
      window.removeEventListener("4xlc:currency-changed", onGameEnded);
    };
  }, [user]);
  return (
    <header
      className="nav-root"
      data-testid="navbar"
      data-pledged={hasPledged ? "true" : "false"}
    >
      <div className="nav-inner">
        <NavLink to="/" className="nav-brand" data-testid="nav-brand">
          <span className="nav-brand-mark">⚜</span>
          <span className="nav-brand-text">4X&nbsp;Legendary&nbsp;Chess</span>
        </NavLink>
        <nav className="nav-links" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                "nav-link" + (isActive ? " nav-link-active" : "")
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/throne-room"
              data-testid="nav-link-throne-room"
              className={({ isActive }) => "nav-link" + (isActive ? " nav-link-active" : "")}
            >
              👑 Throne Room
            </NavLink>
          )}
        </nav>
        {authEnabled && !loading && (
          <div className="nav-auth" data-testid="nav-auth">
            {user ? (
              <>
                {user.picture && (
                  <img
                    src={user.picture}
                    alt=""
                    className="nav-auth-avatar"
                    data-testid="nav-auth-avatar"
                    referrerPolicy="no-referrer"
                  />
                )}
                <span className="nav-auth-name" data-testid="nav-auth-name">
                  {user.name || user.email}
                </span>
                <button
                  type="button"
                  className="nav-auth-btn nav-auth-logout"
                  onClick={logout}
                  data-testid="nav-logout-btn"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                type="button"
                className="nav-auth-btn nav-auth-login"
                onClick={login}
                data-testid="nav-login-btn"
              >
                Sign In with Google
              </button>
            )}
          </div>
        )}
        {authEnabled && !loading && user && currency && (
          // Currency pill is now a Link into the Store, landing on its
          // top-up section (the `store-currency-stack` — the balance +
          // purchase panel shown at the top of the Store page).  Uses
          // a hash anchor so that if/when a dedicated top-up subsection
          // grows a matching id (`#top-up`) the deep-link keeps working
          // without changing this file; today the browser simply
          // navigates to /store and lands at the top, where the balance
          // panel already lives.  Display, value calculation, and poll
          // logic above are unchanged — this only adds a tap action.
          <Link
            to="/store#top-up"
            className="nav-currency"
            data-testid="nav-currency"
            aria-label="Gold and gems balance — tap to open the Store's top-up section"
            title="Open Store — Top up gold & gems"
            style={{ cursor: "pointer", textDecoration: "none" }}
          >
            <span className="nav-currency-gold" data-testid="nav-currency-gold">
              <span className="nav-currency-icon" aria-hidden="true">🪙</span>
              {currency.gold}
            </span>
            <span className="nav-currency-gems" data-testid="nav-currency-gems">
              <span className="nav-currency-icon" aria-hidden="true">💎</span>
              {currency.gems}
            </span>
          </Link>
        )}
      </div>
    </header>
  );
}
